import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import API from "../Network/API";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PostLoader from '../assets/loader.gif';
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import ScheduleSlot from "../components/ScheduleSlot";
import CourseContent from "../Props/CourseContent";
import UserAvatar from "../components/UserAvatar";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../services/currency";
import "./CourseDetail.css";

// "AE" -> 🇦🇪
const countryFlag = (code) =>
  /^[A-Za-z]{2}$/.test(code || "")
    ? String.fromCodePoint(...code.toUpperCase().split("").map((c) => 0x1f1a5 + c.charCodeAt(0)))
    : "";

// "AE" -> "United Arab Emirates" in the current site language
const countryName = (code, lng) => {
  try {
    return new Intl.DisplayNames([lng], { type: "region" }).of(code.toUpperCase());
  } catch (e) {
    return code;
  }
};

const hasText = (html) => !!(html || "").replace(/<[^>]*>|&nbsp;/g, "").trim();

const Stars = ({ rating, className = "" }) => (
  <span className={`cd-stars ${className}`} aria-hidden="true">
    {[1, 2, 3, 4, 5].map((n) => (
      <i key={n} className={`fa-solid fa-star ${Number(rating) >= n - 0.25 ? "is-on" : ""}`} />
    ))}
  </span>
);

// Long rich text clamped to a few lines with a "Show more" toggle.
function ClampedHtml({ html, className = "" }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 4);
  }, [html]);

  return (
    <>
      <div
        ref={ref}
        className={`cd-richtext ${className} ${expanded ? "" : "is-clamped"} ${overflowing && !expanded ? "has-fade" : ""}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {(overflowing || expanded) && (
        <button type="button" className="cd-link-btn" onClick={() => setExpanded(!expanded)}>
          {expanded ? t("courseDetail.show_less") : t("courseDetail.show_more")}
        </button>
      )}
    </>
  );
}

export default function CourseDetail() {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const location = useLocation();
  const isAppRoute = location.pathname.startsWith("/app");
  const { user, accessToken } = useSelector((state) => state.authReducer);
  const prams = useParams();
  const [data, setData] = useState();
  const [isLoading, setLoading] = useState(false);
  const navigate = useNavigate()
  const [slot, setSlot] = useState(null);
  const [lessonId, setLessonId] = useState(null);
  const [needSlot, setNeedSlot] = useState(false);
  const scheduleRef = useRef(null);


  useEffect(() => {

    setLoading(true)
    API.get(`/v1/oauth/course_detail/${prams.slug}`).then((response) => {
      setLoading(false)
      setData(response.data.data.course)
    }).catch((error) => {
      setLoading(false)
    }).finally(
      window.scrollTo(0, 0)
    );
  }, [prams.slug]);

  const checkout = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    localStorage.setItem(
      'data_checkout',
      JSON.stringify({
        course_id: data?.id,
        course_slug: data?.slug,
        course_title: data?.title,
        course_type: data?.course_type,
        lesson_id: data?.trial_lessons?.id,
        price: data?.course_type == 'recorded' ? data?.course_price : data?.trial_lessons?.lesson_price,
        total_review: data?.ratings_count,
        avg_rating: data?.avg_rating,
        teacher_name: data?.teacher?.name,
        teacher_id: data?.teacher?.id,
        teacher_image: data?.teacher?.avatar,
        timezone: timezone,
      })
    )

    if (!accessToken) {
      if (isAppRoute) {
        navigate('/app/login')
      } else {
        navigate('/login')
      }
    } else {
      var price = data?.course_type == 'recorded' ? data?.course_price : data?.trial_lessons?.lesson_price
      if (price == 0 || price == undefined) {
        alert(t("courseDetail.price_not_found"));
        return;
      }
      if (isAppRoute) {
        navigate('/app/checkout')
      } else {
        navigate('/checkout')
      }


    }

  }


  const save_course_fav = (id) => {
    const confirmed = window.confirm(t("courseDetail.confirm_save_favorite"));
    if (confirmed) {
      const fd = new FormData();
      fd.append('course_id', id)
      API.post(`/v1/oauth/student/save-favourite-courses`, fd).then((response) => {
        alert(t('courseDetail.course_saved_successfully'))
      }).catch((error) => {
      });
    }

  }

  const un_save_course_fav = (id) => {
    const confirmed = window.confirm(t("courseDetail.confirm_remove_favorite"));
    if (confirmed) {
      const fd = new FormData();
      fd.append('course_id', id)
      fd.append('type', 'remove')
      API.post(`/v1/oauth/student/save-favourite-courses`, fd).then((response) => {
        alert(t('courseDetail.course_removed'))
      }).catch((error) => {
      });
    }

  }

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: data?.title, url });
      } catch (e) {
        // dismissed by the user
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("courseDetail.link_copied"));
    } catch (e) {
      // clipboard unavailable
    }
  };

  // handle mouse enter
  const handleMouseEnter = (e) => {
    const vid = e.target
    vid.muted = true
    vid.play()
  }
  // handle mouse leave
  const handleMouseLeave = (e) => {
    const vid = e.target
    vid.muted = false
    vid.currentTime = 0
    vid.pause()
  }

  const teacher = data?.teacher;
  const info = teacher?.teacher_info;
  const lng = i18n.resolvedLanguage || i18n.language || "en";
  const avgRating = Number(data?.avg_rating ?? 0);
  const ratingsCount = data?.ratings_count ?? 0;
  const isRecorded = data?.course_type === "recorded";
  // Live courses are booked one lesson at a time; lessons the student already bought are left out.
  const bookableLessons = useMemo(() => {
    const owned = new Set((user?.enrollments || []).map((e) => e.lesson_id));
    return (data?.sections || []).flatMap((section) => section?.lessons || []).filter((lesson) => !owned.has(lesson.id));
  }, [data, user?.enrollments]);
  const bookLesson = bookableLessons.find((lesson) => lesson.id === lessonId)
    || bookableLessons.find((lesson) => lesson.id === data?.trial_lessons?.id)
    || bookableLessons[0];
  const price = isRecorded ? data?.course_price : (bookLesson?.lesson_price ?? data?.trial_lessons?.lesson_price);
  const slotLabel = slot ? `${moment(slot.start).format("ddd, MMM D")} · ${moment(slot.start).format("h:mm A")} – ${moment(slot.end).format("h:mm A")}` : "";

  const scrollToSchedule = () => scheduleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleSlot = (picked) => {
    setSlot(picked);
    if (picked) setNeedSlot(false);
  };

  // Live course: chosen time + lesson -> checkout (same localStorage contract as the checkout page expects).
  const bookNow = () => {
    if (!slot) {
      setNeedSlot(true);
      scrollToSchedule();
      return;
    }
    if (!bookLesson) return;
    localStorage.setItem('lesson', JSON.stringify({ id: bookLesson.id, title: bookLesson.title, price: bookLesson.lesson_price }));
    localStorage.setItem('data_checkout', JSON.stringify({
      course_id: data?.id,
      course_slug: data?.slug,
      course_title: data?.title,
      course_type: data?.course_type,
      lesson_id: bookLesson.id,
      price: bookLesson.lesson_price,
      total_review: data?.ratings_count,
      avg_rating: data?.avg_rating,
      teacher_name: data?.teacher?.name,
      teacher_id: data?.teacher?.id,
      teacher_image: data?.teacher?.avatar,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }));
    const prefix = isAppRoute ? '/app' : '';
    navigate(accessToken ? `${prefix}/checkout` : `${prefix}/login`);
  };
  const languages = (info?.language || "").split(",").map((l) => l.trim()).filter(Boolean);
  const isOwnCourse = teacher?.id != null && teacher?.id === user?.id;
  const isSaved = user?.fav_course?.some((course) => course?.course_id === data?.id);
  // gender_id: 1 male, 2 female, 3 anyone. Logged-out visitors can start booking (checkout asks them to log in).
  const courseGender = Number(data?.gender_id);
  const genderOk = !courseGender || courseGender === 3 || !accessToken || courseGender === Number(user?.gender_id);
  const canBook = genderOk;
  const ratingPct = (star) => {
    const p = data?.rating_percentages;
    return Number((Array.isArray(p) ? p[5 - star] : p?.[star]) ?? 0);
  };
  const formatLabel = isRecorded
    ? t("courseDetail.format_recorded")
    : data?.course_type === "group"
      ? t("courseDetail.format_group")
      : t("courseDetail.format_one_on_one");
  const hasEducation = info?.education_institue || info?.education_level || info?.education_title;

  return (
    <>
      {isLoading ?
        <div className='content' style={{ margin: 'auto', padding: 'auto', textAlign: 'center', height: '500px' }}>
          <img src={PostLoader} alt={t('courseDetail.post_loader')} height={80} width={80} />
        </div>
        :
        <div className="cd-page">
          <div className="container cd-layout">
              <div className="cd-top">

                {/* tutor header */}
                <header className="cd-hero">
                  <UserAvatar src={teacher?.avatar} name={teacher?.name} size={128} className="cd-avatar" />
                  <div className="cd-hero-body">
                    <h1 className="cd-name">{teacher?.name}</h1>
                    <p className="cd-meta">
                      {data?.category && <span>{t("courseDetail.category_tutor", { category: data.category })}</span>}
                      {data?.category && info?.country && <span className="cd-meta-dot" aria-hidden="true">·</span>}
                      {info?.country && (
                        <span>
                          {t("courseDetail.from_country", { country: countryName(info.country, lng) })}
                          <span className="cd-flag" aria-hidden="true">{countryFlag(info.country)}</span>
                        </span>
                      )}
                    </p>
                    {info?.bio && <p className="cd-tagline">{info.bio}</p>}
                  </div>
                </header>

                {/* highlights */}
                <ul className="cd-highlights">
                  {avgRating > 0 && (
                    <li>
                      <span className="cd-hl-icon is-gold"><i className="fa-solid fa-award" /></span>
                      <div>
                        <h3 className="cd-hl-title is-brand">{t("courseDetail.highly_rated")}</h3>
                        <p>{t("courseDetail.rated_by", { rating: data?.avg_rating })}</p>
                      </div>
                    </li>
                  )}
                  <li>
                    <span className="cd-hl-icon"><i className="fa-light fa-graduation-cap" /></span>
                    <div>
                      <h3 className="cd-hl-title">{t("courseDetail.teaches")}</h3>
                      <p>{[data?.category, data?.title].filter(Boolean).join(" · ")}</p>
                    </div>
                  </li>
                  <li>
                    <span className="cd-hl-icon"><i className={`fa-light ${isRecorded ? "fa-circle-play" : "fa-video"}`} /></span>
                    <div>
                      <h3 className="cd-hl-title">{t("courseDetail.lesson_format")}</h3>
                      <p>{formatLabel}</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="cd-main">
                {/* about */}
                <section className="cd-section">
                  <h2 className="cd-h2">{t("courseDetail.about_course")}</h2>
                  <h3 className="cd-course-title">{data?.title}</h3>
                  {hasText(data?.description) && <ClampedHtml html={data?.description} />}
                </section>

                {hasText(data?.requirements) && (
                  <section className="cd-section">
                    <h2 className="cd-h2">{t("courseDetail.requirements")}</h2>
                    <ClampedHtml html={data?.requirements} />
                  </section>
                )}

                {languages.length > 0 && (
                  <section className="cd-section">
                    <h2 className="cd-h2">{t("courseDetail.i_speak")}</h2>
                    <div className="cd-chips">
                      {languages.map((l) => <span key={l} className="cd-chip">{l}</span>)}
                    </div>
                  </section>
                )}

                {isRecorded ? (
                  <section className="cd-section">
                    <CourseContent data={data} />
                  </section>
                ) : data != null && (
                  <section className="cd-section" ref={scheduleRef} id="schedule">
                    <h2 className="cd-h2">{t("courseDetail.schedule")}</h2>
                    <p className="cd-muted mb-3">{t("courseDetail.available_slots")}</p>
                    {needSlot && !slot &&
                      <div className="cd-alert"><i className="fa-light fa-circle-info" />{t("courseDetail.pick_time_first")}</div>
                    }
                    <ScheduleSlot schedule={data?.teacher_schedules} onSelect={handleSlot} />

                    {slot && bookLesson && (
                      <div className="cd-confirm">
                        <div className="cd-confirm-rows">
                          <div className="cd-confirm-row">
                            <i className="fa-light fa-calendar-check" />
                            <div>
                              <span className="cd-confirm-label">{t("courseDetail.your_lesson_time")}</span>
                              <strong>{slotLabel}</strong>
                            </div>
                          </div>
                          <div className="cd-confirm-row">
                            <i className="fa-light fa-book-open" />
                            <div className="cd-confirm-grow">
                              <span className="cd-confirm-label">{t("courseDetail.lesson")}</span>
                              {bookableLessons.length > 1 ? (
                                <select className="form-select cd-confirm-select" value={bookLesson.id} onChange={(e) => setLessonId(Number(e.target.value))}>
                                  {bookableLessons.map((lesson) => (
                                    <option key={lesson.id} value={lesson.id}>
                                      {lesson.title} — {formatPrice(lesson.lesson_price)}{Number(lesson.is_trial) === 1 ? ` (${t("courseDetail.trial")})` : ""}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <strong>{bookLesson.title}</strong>
                              )}
                            </div>
                          </div>
                        </div>

                        {canBook ? (
                          <>
                            <button type="button" className="cd-btn-primary cd-confirm-btn" onClick={bookNow}>
                              <i className="fa-light fa-lock" />
                              {t("courseDetail.confirm_and_pay", { price: formatPrice(bookLesson.lesson_price) })}
                            </button>
                            <p className="cd-confirm-note">
                              {accessToken ? t("courseDetail.pay_next_step") : t("courseDetail.login_to_continue")}
                            </p>
                          </>
                        ) : (
                          <div className="cd-alert cd-alert-warn mb-0">
                            <i className="fa-light fa-circle-exclamation" />{t(courseGender === 1 ? "courseDetail.male_only" : "courseDetail.female_only")}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                )}

                {/* reviews */}
                <section className="cd-section">
                  <h2 className="cd-h2">{t("courseDetail.reviews_tab", { count: ratingsCount })}</h2>
                  {ratingsCount > 0 ? (
                    <>
                      <div className="cd-rating-summary">
                        <div className="cd-rating-score">
                          <span className="cd-rating-big">{avgRating.toFixed(1)}</span>
                          <Stars rating={avgRating} />
                          <span className="cd-muted">{ratingsCount == 1 ? t('courseDetail.review_count_single', { count: ratingsCount }) : t('courseDetail.reviews_count', { count: ratingsCount })}</span>
                        </div>
                        <div className="cd-rating-bars">
                          {[5, 4, 3, 2, 1].map((star) => (
                            <div className="cd-bar-row" key={star}>
                              <span className="cd-bar-label">{star} <i className="fa-solid fa-star" /></span>
                              <span className="cd-bar"><span style={{ width: `${ratingPct(star)}%` }} /></span>
                              <span className="cd-bar-pct">{Math.round(ratingPct(star))}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="cd-reviews">
                        {data?.ratings.map((review) => (
                          <article className="cd-review" key={review.id}>
                            <div className="cd-review-head">
                              <UserAvatar src={review?.student?.avatar} name={review?.student?.name} size={44} className="cd-review-avatar" />
                              <div>
                                <h4 className="cd-review-name">{review?.student?.name}</h4>
                                <span className="cd-muted cd-small">{review?.created_at}</span>
                              </div>
                              <Stars rating={review.rating} className="ms-auto" />
                            </div>
                            {review?.comment && <p className="cd-review-text">{review.comment}</p>}
                          </article>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="cd-empty">
                      <i className="fa-light fa-star" />
                      <p>{t("courseDetail.no_reviews")}</p>
                    </div>
                  )}
                </section>

                {hasEducation && (
                  <section className="cd-section">
                    <h2 className="cd-h2">{t("courseDetail.resume")}</h2>
                    <div className="cd-resume-tabs"><span className="is-active">{t("courseDetail.education")}</span></div>
                    <div className="cd-resume-row">
                      <span className="cd-resume-icon"><i className="fa-light fa-building-columns" /></span>
                      <div>
                        {info?.education_institue && <h4 className="cd-resume-title">{info.education_institue}</h4>}
                        <p className="cd-muted mb-0">{[info?.education_level, info?.education_title].filter(Boolean).join(" · ")}</p>
                      </div>
                    </div>
                  </section>
                )}
              </div>

              {/* booking card */}
              <aside className="cd-side">
                <div className="cd-card">
                  {(data?.cover_video || data?.cover_image) && (
                    <div className="cd-media">
                      {data?.cover_video ?
                        <video key={0}
                          controls={true}
                          preload="metadata"
                          type="video/mp4"
                          poster={data.cover_image || undefined}
                          src={`${data?.cover_video}`}
                          className="w-100 video-hover-play"
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        />
                        :
                        <div className="cd-cover" style={{ backgroundImage: `url(${data.cover_image})` }} />
                      }
                    </div>
                  )}

                  <div className="cd-price">
                    <span className="cd-price-amount">{formatPrice(price ?? 0)}</span>
                    <span className="cd-price-unit">{isRecorded ? t('courseDetail.full_course') : t('courseDetail.per_lesson')}</span>
                  </div>

                  <div className="cd-stats">
                    <div>
                      <strong><i className="fa-solid fa-star" />{avgRating > 0 ? avgRating.toFixed(1) : t('courseDetail.new')}</strong>
                      <span>{ratingsCount == 1 ? t('courseDetail.review_count_single', { count: ratingsCount }) : t('courseDetail.reviews_count', { count: ratingsCount })}</span>
                    </div>
                    <div>
                      <strong>{data?.lessons_count ?? 0}</strong>
                      <span>{t("courseDetail.lessons_label")}</span>
                    </div>
                    <div>
                      <strong>{data?.enrollments_count ?? 0}</strong>
                      <span>{t("courseDetail.students_label")}</span>
                    </div>
                  </div>

                  {isRecorded ? (
                    !user?.enrollments?.some(enroll => enroll.course_id === data?.id) && canBook && (
                      <button onClick={() => checkout()} type="button" className="cd-btn-primary">
                        <i className="fa-light fa-cart-shopping" />
                        {t("courseDetail.buy_this_course")}
                      </button>
                    )
                  ) : !canBook ? (
                    <div className="cd-alert cd-alert-warn mb-0">
                      <i className="fa-light fa-circle-exclamation" />{t(courseGender === 1 ? "courseDetail.male_only" : "courseDetail.female_only")}
                    </div>
                  ) : slot ? (
                    <>
                      <div className="cd-card-slot">
                        <i className="fa-light fa-calendar-check" />
                        <span>{slotLabel}</span>
                        <button type="button" className="cd-link-btn" onClick={scrollToSchedule}>{t("courseDetail.change")}</button>
                      </div>
                      <button onClick={bookNow} type="button" className="cd-btn-primary">
                        <i className="fa-light fa-lock" />
                        {t("courseDetail.continue_to_payment")}
                      </button>
                    </>
                  ) : (
                    <button onClick={bookNow} type="button" className="cd-btn-primary">
                      <i className="fa-light fa-calendar-check" />
                      {t("courseDetail.book_a_lesson")}
                    </button>
                  )}

                  <div className="cd-actions">
                    {!isOwnCourse && (
                      <>
                        <Link to={`/home/chat/conversation/${teacher?.id}`} className="cd-icon-btn" title={t("courseDetail.message_tutor")} aria-label={t("courseDetail.message_tutor")}>
                          <i className="fa-light fa-message-lines" />
                        </Link>
                        {isSaved ?
                          <button onClick={() => un_save_course_fav(data?.id)} type="button" className="cd-icon-btn is-saved" title={t("courseDetail.remove_from_wishlist")} aria-label={t("courseDetail.remove_from_wishlist")}>
                            <i className="fa-solid fa-heart" />
                          </button>
                          :
                          <button onClick={() => save_course_fav(data?.id)} type="button" className="cd-icon-btn" title={t("courseDetail.add_to_wishlist")} aria-label={t("courseDetail.add_to_wishlist")}>
                            <i className="fa-light fa-heart" />
                          </button>
                        }
                      </>
                    )}
                    <button onClick={share} type="button" className="cd-icon-btn" title={t("courseDetail.share")} aria-label={t("courseDetail.share")}>
                      <i className="fa-light fa-arrow-up-from-bracket" />
                    </button>
                  </div>

                  <div className="cd-note">
                    <i className="fa-solid fa-shield-check" />
                    <div>
                      <strong>{t("courseDetail.secure_checkout")}</strong>
                      <span>{data?.lessons_count == 1 ? t('courseDetail.lesson_included_single', { count: data?.lessons_count }) : t('courseDetail.lessons_included', { count: data?.lessons_count ?? 0 })}</span>
                    </div>
                  </div>
                </div>
              </aside>
          </div>
        </div>
      }

      {/* phones/tablets: the booking card sits above the schedule, so keep the next step in reach */}
      {!isLoading && !isRecorded && slot && canBook && bookLesson && (
        <>
        <div className="cd-mobile-bar-spacer d-lg-none" />
        <div className={`cd-mobile-bar d-lg-none ${isAppRoute ? 'is-app' : ''}`}>
          <div className="cd-mobile-bar-info">
            <strong>{formatPrice(bookLesson.lesson_price)}</strong>
            <span>{slotLabel}</span>
          </div>
          <button type="button" className="cd-btn-primary" onClick={bookNow}>{t("courseDetail.continue_to_payment")}</button>
        </div>
        </>
      )}
    </>
  )

}
